import React from 'react'
import styled from 'styled-components'
import NumberFormat from 'react-number-format'
import { Button } from '@blueprintjs/core'
import { mobile } from '../../../common/assets/utils/_breakpoints'
import { gray, white, green } from '../../../ui/utils/_colors'
import { formatBRL } from '../../common/formatBRL'
import { calculateDownPayment, isValueInValidPriceRange } from '../../../organisms/ProductSale/components/ProductsHelper'
import CustomButton from '../../../ui/Button/CustomButton'

interface Props {
  productPrice: number
  maxPrice: number
  minPriceRate: number | undefined
  downPaymentRate: number
  onConfirm: (
    downPayment: number, 
    purchaseValueMinusDownPayment: number,
    productPrice: number
  ) => void
  onValueChange: () => void
}

interface InfoTextProps {
  $isValidPrice: boolean;
  $hasInput: boolean;
}

interface PriceInputProps {
  $isValidPrice: boolean;
  $hasInput: boolean;
}

export const ProductPriceRangeInputSection: React.FC<Props> = props => {
  const {
    productPrice,
    maxPrice,
    minPriceRate,
    downPaymentRate,
    onConfirm,
    onValueChange,
  } = props

  const [inputProductValue, setInputProductValue] = React.useState<string>()
  const [purchaseValueMinusDownPayment, setPurchaseValueMinusDownPayment] = React.useState<number>()
  const [inputProductValueAsNumber, setInputProductValueAsNumber] = React.useState<number>(0)
  const [downPayment, setDownPayment] = React.useState<number>(0)
  const minPrice = productPrice * (minPriceRate ?? 1)
  
  const handleValueChange = (values: { value: string }) => {
    const inputValue = values.value
    setInputProductValue(inputValue)
    
    const inputValueAsNumber = inputValue ? Number(inputValue) : 0
    setInputProductValueAsNumber(inputValueAsNumber)
    
    const calculatedDownPayment = calculateDownPayment(inputValueAsNumber, downPaymentRate ?? 0)
    setDownPayment(calculatedDownPayment)
    
    const purchaseValueMinusDownPayment = inputValueAsNumber - calculatedDownPayment
    setPurchaseValueMinusDownPayment(purchaseValueMinusDownPayment)

    onValueChange()
  }

  const handleConfirm = () => {
    if (isValueInValidPriceRange(inputProductValueAsNumber!!, minPrice, maxPrice)) {
      onConfirm(
        downPayment, 
        purchaseValueMinusDownPayment!!, 
        inputProductValueAsNumber
      )
    }
  }

  const isValidPrice = isValueInValidPriceRange(inputProductValueAsNumber, minPrice, maxPrice)
  const isConfirmButtonDisabled = !inputProductValue || !isValidPrice

  const getProductValueInputMessage = () => {
    if (!inputProductValue) {
      return <>Informe um valor de até <strong>{formatBRL(maxPrice)}</strong></>
    }
    
    if (inputProductValueAsNumber < minPrice) {
      return <>Valor do aparelho deve ser no mínimo <strong>{formatBRL(minPrice)}</strong></>
    }
    
    if (inputProductValueAsNumber > maxPrice) {
      return <>Valor do aparelho deve ser no máximo <strong>{formatBRL(maxPrice)}</strong></>
    }
    
    return <>Informe um valor de até <strong>{formatBRL(maxPrice)}</strong></>
  }

  return (
    <Container>
      <Title>Valores da venda</Title>
      
      <InputContainer>
        <Label>Valor do aparelho</Label>
        <PriceInput>
          <NumberFormat
            placeholder="Valor do aparelho"
            value={inputProductValue}
            onValueChange={handleValueChange}
            thousandSeparator="."
            decimalSeparator=","
            prefix="R$ "
            decimalScale={2}
            fixedDecimalScale
            allowEmptyFormatting={false}
            className={`bp3-input-group bp3-input ${
              inputProductValue && !isValidPrice ? 'bp3-intent-danger' : 'bp3-intent-none' 
            } `}
            isNumericString={true}
            allowNegative={false}
            autoComplete="off"
            tabIndex={1}
          />
        </PriceInput>
      </InputContainer>
      <InfoText $isValidPrice={isValidPrice} $hasInput={!!inputProductValue}>
        {getProductValueInputMessage()}
      </InfoText>

      <PriceBreakdown>
        <PriceRow>
          <span>Entrada fixa ({downPaymentRate * 100}%)</span>
          <strong>
            {inputProductValue ? formatBRL(downPayment) : '-'}
          </strong>
        </PriceRow>
        <PriceRow>
          <span>Valor financiado</span>
          <strong>
            {inputProductValue ? formatBRL(inputProductValueAsNumber - downPayment) : '-'}
          </strong>
        </PriceRow>
      </PriceBreakdown>

      <ConfirmButton
        borderRadius="8px"
        onClick={handleConfirm}
        disabled={isConfirmButtonDisabled}
      >
        Escolher parcelamento
      </ConfirmButton>
    </Container>
  )
}

const Container = styled.div`
  width: 90%;
  padding: 20px;
  border-radius: 20px;
  background: white;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin: 20px;
  box-sizing: border-box;
  overflow: hidden;
`

const Title = styled.h2`
  font-family: Inter;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 24px;
  text-align: center;
  width: 100%;
  
  @media (max-width: ${mobile}) {
    font-size: 20px;
    margin-bottom: 16px;
  }
`

const Label = styled.label`
  font-family: Inter;
  font-size: 16px;
  font-weight: bold;
  white-space: nowrap;
  
  @media (max-width: 584px) {
    text-align: start;
  }

  @media (max-width: ${mobile}) {
    font-size: 14px;
    width: 100%;
    text-align: start;
  }
`

const InputContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  box-sizing: border-box;

  @media (max-width: 584px) {
    gap: 8px;
    display: grid;
    justify-content: start;
  }
`

const PriceInput = styled.div`
  width: 220px;
  min-width: 180px;
  display: flex;
  justify-content: flex-end;
  margin-left: auto;
  font-family: Inter;
  
  .bp3-input {
    width: 100%;
    height: 40px;
    font-size: 16px;
    border: 1px solid ${white.light};
    border-radius: 4px;
    padding: 8px 12px;
    text-align: right;
    box-sizing: border-box;
    
    &::placeholder {
      color: ${gray.light};
      text-align: left;
    }
  }

  @media (max-width: ${mobile}) {
    margin-left: 0;
    width: 100%;
    min-width: 100%;
    max-width: 280px;
    margin: 0 auto;
    
    .bp3-input {
      height: 48px;
      font-size: 16px;
      padding: 8px 12px;
    }
  }
`

const InfoText = styled.p<InfoTextProps>`
  color: ${(props: InfoTextProps) => (!props.$isValidPrice && props.$hasInput) ? '#FF4444' : '#666'};
  font-size: 14px;
  font-family: Inter;
  margin: 0;
  text-align: right;
  transition: color 0.2s ease;
  width: 100%;
  box-sizing: border-box;
  word-break: break-word;

  strong {
    font-weight: bold;
    color: inherit;
  }
  
  @media (max-width: 584px) {
    font-size: 12px;
    text-align: left;
  }
`

const PriceBreakdown = styled.div`
  padding: 16px 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: ${mobile}) {
    padding: 12px 0;
  }
`

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
  font-family: Inter;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  span {
    color: #666;
    font-size: 16px;
  }
  
  strong {
    font-weight: 600;
    text-align: right;
  }

  @media (max-width: ${mobile}) {
    margin-bottom: 12px;
    gap: 4px;
    
    span, strong {
      font-size: 14px;
    }
  }
`

const ConfirmButton = styled(CustomButton)`
  height: 56px;
  font-size: 18px;
  font-family: Inter;
  width: 100%;
  border-color: ${green.primary};
  background-color: ${green.primary};
  color: ${white.primary};

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
  @media (max-width: ${mobile}) {
    margin-top: 16px;
    height: 40px;
    font-size: 14px;
  }
`

export default ProductPriceRangeInputSection